import { useMemo, useState } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentType,
  ComplianceProgramStep,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import InfoTooltip from "@components/tooltip/info-tooltip";
import WarningTooltip from "@components/tooltip/warning-tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import TalentIndicatorSidebar from "../talent-indicator-sidebar/talent-indicator-sidebar";
import { ProgramForm, ProgramFormStep } from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import CoreValueAssessmentSettingsModal from "./core-value-assessment-settings-modal";
import GoalAssessmentSettingsModal from "./goal-assessment-settings-modal";
import QuestionSetSelector from "./question-set-selector";
import RoleBasedAssessmentSettingsModal from "./role-based-assessment-settings-modal";

const SelfManagerAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  talentIndicatorData,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  onAddOrUpdateStep,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const isAdmin = isAdminVar();
  const hasGoalBasedAssessments = organization.featureFlags.goals;

  const [isRoleBasedAssessmentModalOpen, setIsRoleBasedAssessmentModalOpen] =
    useState(false);
  const [isGoalBasedAssessmentModalOpen, setIsGoalBasedAssessmentModalOpen] =
    useState(false);
  const [
    isCoreValueBasedAssessmentModalOpen,
    setIsCoreValueBasedAssessmentModalOpen,
  ] = useState(false);
  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [isTalentIndicatorSidebarOpen, setIsTalentIndicatorSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(
        ({ assessmentType }) => assessmentType === AssessmentType.Performance,
      )
      .filter((assessmentGroup) => !assessmentGroup.hasNpsQuestions)
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const downwardReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.DownwardReview,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return downwardReviewStep && downwardReviewStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === downwardReviewStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, downwardReviewStep]);

  const applicableTalentIndicators = useMemo(() => {
    return talentIndicatorData
      ? talentIndicatorData.filter(
          (talentIndicator) => !talentIndicator.isManagerIndicator,
        )
      : [];
  }, [talentIndicatorData]);

  return (
    downwardReviewStep && (
      <>
        {isTalentIndicatorSidebarOpen && (
          <TalentIndicatorSidebar
            onClose={() => setIsTalentIndicatorSidebarOpen(false)}
            canUpdate={canUpdate}
            assessmentType={AssessmentType.Performance}
            talentIndicatorData={applicableTalentIndicators}
            talentIndicatorSetup={
              downwardReviewStep?.talentIndicatorReviewConfig ?? {
                talentIndicators: [],
              }
            }
            onUpdateTalentIndicators={(talentIndicatorReviewConfig) => {
              onAddOrUpdateStep({
                ...downwardReviewStep,
                talentIndicatorReviewConfig,
              });
            }}
          />
        )}
        {isAssessmentGroupSidebarOpen && (
          <AssessmentGroupSidebar
            assessmentType={AssessmentType.Performance}
            assessmentGroupId={selectedAssessmentGroup?.id}
            assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
            onClose={() => {
              setAssessmentGroupToDuplicate(null);
              setIsAssessmentGroupSidebarOpen(false);
            }}
            onAssessmentGroupCreated={(assessmentGroup) => {
              onAssessmentGroupCreated(assessmentGroup);
              onAddOrUpdateStep({
                ...downwardReviewStep,
                questionSetIds: [assessmentGroup.id],
              });
            }}
            onAssessmentGroupArchived={(assessmentGroupId) =>
              onAssessmentGroupArchived(assessmentGroupId)
            }
          />
        )}
        {isRoleBasedAssessmentModalOpen && (
          <RoleBasedAssessmentSettingsModal
            assessmentType={AssessmentType.Performance}
            onClose={() => setIsRoleBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={downwardReviewStep}
            onChangeSettings={(settings) => {
              onAddOrUpdateStep({
                ...downwardReviewStep,
                roleBasedReviewConfig: {
                  ...downwardReviewStep.roleBasedReviewConfig,
                  ...settings,
                },
              });
            }}
          />
        )}
        {isGoalBasedAssessmentModalOpen && (
          <GoalAssessmentSettingsModal
            assessmentType={AssessmentType.Performance}
            onClose={() => setIsGoalBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={downwardReviewStep}
            onChangeSettings={(settings) => {
              onAddOrUpdateStep({
                ...downwardReviewStep,
                goalReviewConfig: {
                  ...downwardReviewStep.goalReviewConfig,
                  ...settings,
                },
              });
            }}
          />
        )}
        {isCoreValueBasedAssessmentModalOpen && (
          <CoreValueAssessmentSettingsModal
            assessmentType={AssessmentType.Performance}
            onClose={() => setIsCoreValueBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={downwardReviewStep}
            onChangeSettings={(settings) => {
              onAddOrUpdateStep({
                ...downwardReviewStep,
                coreValueReviewConfig: {
                  ...downwardReviewStep.coreValueReviewConfig,
                  ...settings,
                },
              });
            }}
          />
        )}
        <ComplianceProgramFormStep
          title={`Self and Manager ${label("review", {
            capitalize: true,
          })}`}
        >
          <div className="px-6 pb-6 flex flex-col gap-3">
            <div className="flex items-center gap-3 p-4 bg-white border rounded">
              <QuestionSetSelector
                assessmentType={AssessmentType.Performance}
                availableAssessmentGroupSelectOptions={
                  availableAssessmentGroupSelectOptions
                }
                canUpdate={canUpdate}
                onUpdateAssessmentTemplate={(assessmentTemplateId) => {
                  onAddOrUpdateStep({
                    ...downwardReviewStep,
                    questionSetIds: assessmentTemplateId
                      ? [assessmentTemplateId]
                      : [],
                  });
                }}
                onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
                onOpenAssessmentGroupSidebar={() =>
                  setIsAssessmentGroupSidebarOpen(true)
                }
                selectedAssessmentGroup={selectedAssessmentGroup}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">{`Role ${label("review", {
                  capitalize: true,
                })}`}</div>
                <InfoTooltip text="Use this if you would like to assess an individual based on their job responsibilities and competencies." />
              </div>

              {!organization.hasCareerFramework ? (
                <WarningTooltip
                  size={6}
                  text={`There are no Career Tracks defined for your ${label(
                    "organization",
                  )}.${
                    isAdmin
                      ? ` Go to ${label("organization", {
                          capitalize: true,
                        })} settings to define them.`
                      : ""
                  }`}
                />
              ) : (
                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    disabled={
                      !downwardReviewStep ||
                      !downwardReviewStep.roleBasedReviewConfig
                        .enabledForCurrentRole
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsRoleBasedAssessmentModalOpen(true)}
                  />
                  <ToggleSwitch
                    disabled={!canUpdate || !downwardReviewStep}
                    checked={
                      !!downwardReviewStep?.roleBasedReviewConfig
                        .enabledForCurrentRole
                    }
                    onChange={(enabled) =>
                      onAddOrUpdateStep({
                        ...downwardReviewStep,
                        roleBasedReviewConfig: {
                          ...downwardReviewStep.roleBasedReviewConfig,
                          enabledForCurrentRole: enabled,
                        },
                      })
                    }
                  />
                </div>
              )}
            </div>

            {hasGoalBasedAssessments && (
              <div className="flex items-center justify-between p-4 bg-white border rounded">
                <div className="flex items-center gap-2">
                  <div className="text-sm">{`${label("goal", {
                    capitalize: true,
                  })} ${label("review", {
                    capitalize: true,
                  })}`}</div>
                  <InfoTooltip
                    text={`Use this if you would like to assess an individual's progress towards their ${label(
                      "goal",
                      {
                        pluralize: true,
                      },
                    )}.`}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    disabled={
                      !downwardReviewStep ||
                      !downwardReviewStep?.goalReviewConfig.enabled
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsGoalBasedAssessmentModalOpen(true)}
                  />
                  <ToggleSwitch
                    disabled={!canUpdate || !downwardReviewStep}
                    checked={!!downwardReviewStep?.goalReviewConfig.enabled}
                    onChange={(enabled) =>
                      onAddOrUpdateStep({
                        ...downwardReviewStep,
                        goalReviewConfig: {
                          ...downwardReviewStep.goalReviewConfig,
                          enabled,
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">{`Values ${label("review", {
                  capitalize: true,
                })}`}</div>
                <InfoTooltip text="Use this if you would like to assess an individual's alignment with your Core Values" />
              </div>

              {organization.coreValues?.edges.length === 0 ? (
                <WarningTooltip
                  size={6}
                  text={`There are no Core Values defined for your ${label(
                    "organization",
                  )}.${
                    isAdmin
                      ? ` Go to ${label("organization", {
                          capitalize: true,
                        })} settings to define them.`
                      : ""
                  }`}
                />
              ) : (
                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    disabled={
                      !downwardReviewStep ||
                      !downwardReviewStep?.coreValueReviewConfig.enabled
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsCoreValueBasedAssessmentModalOpen(true)}
                  />
                  <ToggleSwitch
                    disabled={!canUpdate || !downwardReviewStep}
                    checked={
                      !!downwardReviewStep?.coreValueReviewConfig.enabled
                    }
                    onChange={(enabled) =>
                      onAddOrUpdateStep({
                        ...downwardReviewStep,
                        coreValueReviewConfig: {
                          ...downwardReviewStep.coreValueReviewConfig,
                          enabled,
                        },
                      })
                    }
                  />
                </div>
              )}
            </div>

            {waffle.flag_is_active("talent-indicators") && (
              <div className="flex items-center justify-between p-4 bg-white border rounded">
                <div className="flex items-center gap-2">
                  <div className="text-sm">Employee Talent Indicators</div>
                  <InfoTooltip text="Use this if you would like to assess employees on standard talent indicators." />
                </div>

                {applicableTalentIndicators.length === 0 ? (
                  <WarningTooltip
                    size={6}
                    text="There was a problem loading talent indicators"
                  />
                ) : (
                  <div className="flex items-center gap-4">
                    <Button
                      className="mb-1"
                      aria-label="Edit employee talent indicators"
                      disabled={
                        !downwardReviewStep ||
                        !downwardReviewStep?.talentIndicatorReviewConfig ||
                        downwardReviewStep?.talentIndicatorReviewConfig
                          .talentIndicators?.length === 0
                      }
                      icon={TopicflowIconMap.edit}
                      theme={buttonTheme.iconGray}
                      onClick={() => setIsTalentIndicatorSidebarOpen(true)}
                    />
                    <ToggleSwitch
                      aria-label="Employee talent indicator toggle"
                      disabled={!canUpdate || !downwardReviewStep}
                      checked={
                        !!downwardReviewStep?.talentIndicatorReviewConfig &&
                        downwardReviewStep.talentIndicatorReviewConfig
                          .talentIndicators.length > 0
                      }
                      onChange={(enabled) => {
                        onAddOrUpdateStep({
                          ...downwardReviewStep,
                          talentIndicatorReviewConfig: {
                            ...downwardReviewStep.talentIndicatorReviewConfig,
                            talentIndicators: enabled
                              ? applicableTalentIndicators.map(
                                  (talentIndicator) => ({
                                    talentIndicatorId: talentIndicator.id,
                                    responses:
                                      AssessmentQuestionResponses.ExcludeSelfAssessment,
                                    responseVisibility:
                                      AssessmentQuestionResponseVisibility.HiddenFromSubject,
                                    comment: AssessmentQuestionComment.Optional,
                                    description: talentIndicator.description,
                                  }),
                                )
                              : [],
                          },
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </ComplianceProgramFormStep>
      </>
    )
  );
};
export default SelfManagerAssessmentStep;
